import { Button } from "@heroui/button";
import { cn } from "@heroui/react";

import { logger } from "@/lib/secure-logger";
import { Recommendation } from "@/types/dataSchema";
import { ContactModal } from "@/components/ContactModal";
import { NotifyModal } from "@/components/NotifyModal";
import { useAdminStatus } from "@/app/hooks/useAdminStatus";

import { useAnswerContext } from "./AnswerContext";
import FeedbackWidget from "./FeedbackWidget";
import { computeRecommendationKeys } from "./computeRecommendationKeys";
import { getNextQuestionId } from "./nextQuestionAlgorithm";
import { RecommendationCard } from "./components/RecommendationCard";
import { RecommendationActions } from "./components/RecommendationActions";
import {
  useRecommendationHandlers,
  useCardAnimations,
} from "./hooks/useRecommendationHandlers";
import { getCurrentValidPath } from "./utils/recommendationUtils";

export default function RecommendationsDisplay() {
  const { answers, journeyData, currentQuestionId } = useAnswerContext();
  const { isAdmin, showRecommendations } = useAdminStatus();
  const recommendations =
    journeyData?.recommendations || ([] as Recommendation[]);

  const validPath = getCurrentValidPath(
    journeyData,
    currentQuestionId,
    answers,
    getNextQuestionId,
  );

  const {
    // Modal states
    contactModal,
    generalContactModal,
    setGeneralContactModal,
    notifyModal,

    // Recommendation states
    notifiedRecommendations,
    contactedRecommendations,

    // Animation states
    revealedCards,
    setRevealedCards,
    shinyCards,
    setShinyCards,
    replayTrigger,
    showAllRecommendations,
    setShowAllRecommendations,
    additionalCardsRevealed,
    setAdditionalCardsRevealed,
    animationStarted,
    setAnimationStarted,

    // Handlers
    handleContactClick,
    handleContactModalClose,
    handleContactSubmit,
    handleGeneralContactSubmit,
    handleNotifyClick,
    handleNotifyModalClose,
    handleNotifySubmit,
    handleTitleDoubleClick,
  } = useRecommendationHandlers();

  const recommendedRecommendationsKeys = computeRecommendationKeys(
    journeyData?.questions || [],
    answers,
    recommendations,
    validPath,
  );

  logger.debug("Recommendations calculated", {
    recommendedCount: recommendedRecommendationsKeys.length,
  });

  const allRecommendedRecommendations = recommendations
    .filter((recommendation) =>
      recommendedRecommendationsKeys
        .filter((s) => !s.excluded_by_answers.length)
        .map((s) => s.id)
        .includes(recommendation.id),
    )
    .sort((a, b) => {
      // by default, sort by score (descending)
      const aScore =
        recommendedRecommendationsKeys.find((s) => s.id === a.id)?.score || 0;
      const bScore =
        recommendedRecommendationsKeys.find((s) => s.id === b.id)?.score || 0;

      return bScore - aScore;
    });

  const recommendedRecommendations = allRecommendedRecommendations.slice(0, 3);
  const additionalRecommendations = showAllRecommendations
    ? allRecommendedRecommendations.slice(3)
    : [];
  const allDisplayedRecommendations = [
    ...recommendedRecommendations,
    ...additionalRecommendations,
  ];

  const additionalRecommendationsCount = Math.max(
    0,
    allRecommendedRecommendations.length - 3,
  );

  // Check if there are any contact recommendations in the recommended recommendations
  const hasContactRecommendations = allDisplayedRecommendations.some(
    (recommendation) => recommendation.type === "contact",
  );

  // Use animation hooks
  useCardAnimations(
    recommendedRecommendations,
    additionalRecommendations,
    replayTrigger,
    showAllRecommendations,
    setRevealedCards,
    setShinyCards,
    setAnimationStarted,
    setAdditionalCardsRevealed,
  );

  return (
    <div className={cn("p-6", "lg:ml-[33%] lg:w-[67%] overflow-auto")}>
      <h2
        className={cn(
          "text-3xl",
          "font-semibold",
          "text-title-color",
          "text-center",
          "lg:mt-20",
          "cursor-pointer select-none", // Visual hint for interactivity
        )}
        title="Double-click to replay the animation" // Tooltip for accessibility
        onDoubleClick={handleTitleDoubleClick}
      >
        Here we go! 🎉
      </h2>
      <p
        className={cn(
          "text-xl",
          "text-description-color",
          "text-center",
          "my-10",
        )}
      >
        {recommendedRecommendations.length > 0
          ? `Here ${recommendedRecommendations.length === 1 ? "is" : "are"} ${recommendedRecommendations.length} recommendation${recommendedRecommendations.length === 1 ? "" : "s"} based on your answers:`
          : "Unfortunately, we couldn't find any recommendations based on your answers."}
      </p>

      <div className={cn("pl-6", "space-y-10 3xl:w-2/3 mx-auto")}>
        {recommendedRecommendations.map((recommendation, index) => (
          <RecommendationCard
            key={recommendation.id}
            animationStarted={animationStarted}
            contactedRecommendations={contactedRecommendations}
            index={index}
            isAdmin={isAdmin}
            isRevealed={revealedCards.has(index)}
            isShiny={shinyCards.has(index)}
            journeyData={journeyData}
            notifiedRecommendations={notifiedRecommendations}
            recommendation={recommendation}
            recommendedRecommendationsKeys={recommendedRecommendationsKeys}
            showRecommendations={showRecommendations}
            onContactClick={handleContactClick}
            onNotifyClick={handleNotifyClick}
          />
        ))}
      </div>

      {/* Additional recommendations - only shown when expanded */}
      {showAllRecommendations && additionalRecommendations.length > 0 && (
        <div className={cn("pl-6", "space-y-10 3xl:w-2/3 mx-auto mt-10")}>
          {additionalRecommendations.map((recommendation, index) => {
            const actualIndex = recommendedRecommendations.length + index;

            return (
              <RecommendationCard
                key={recommendation.id}
                animationStarted={true}
                contactedRecommendations={contactedRecommendations}
                index={actualIndex}
                isAdditional={true}
                isAdmin={isAdmin}
                isRevealed={additionalCardsRevealed.has(actualIndex)}
                isShiny={false}
                journeyData={journeyData}
                notifiedRecommendations={notifiedRecommendations}
                recommendation={recommendation}
                recommendedRecommendationsKeys={recommendedRecommendationsKeys}
                showRecommendations={showRecommendations}
                onContactClick={handleContactClick}
                onNotifyClick={handleNotifyClick}
              />
            );
          })}
        </div>
      )}

      {/* Additional Recommendations Toggle - Ultra Minimal */}
      {additionalRecommendationsCount > 0 && (
        <div className="mt-8 mb-6 text-center">
          <Button
            className="text-gray-500 hover:text-gray-300 bg-transparent hover:bg-transparent border-none font-normal px-3 py-1 text-sm transition-colors duration-200 underline decoration-dotted underline-offset-4 hover:decoration-solid"
            size="sm"
            variant="light"
            onPress={() => {
              setShowAllRecommendations(!showAllRecommendations);
              if (showAllRecommendations) {
                // Reset additional cards when hiding them
                setAdditionalCardsRevealed(new Set());
              }
            }}
          >
            {showAllRecommendations
              ? "Show less"
              : `Show ${additionalRecommendationsCount} more option${additionalRecommendationsCount > 1 ? "s" : ""}`}
          </Button>
          {!showAllRecommendations && (
            <p className="text-gray-600 text-xs mt-1">
              Additional recommendations that might be less relevant
            </p>
          )}
        </div>
      )}

      {/* Final Actions Section */}
      <RecommendationActions
        answers={answers}
        hasContactRecommendations={hasContactRecommendations}
        journeyData={journeyData}
        recommendations={allDisplayedRecommendations}
        onGeneralContactClick={() => setGeneralContactModal(true)}
      />

      {/* Feedback Widget */}
      <FeedbackWidget />

      {/* Contact Modal */}
      <ContactModal
        actionName={contactModal.action?.name || ""}
        isOpen={contactModal.isOpen}
        recommendationName={contactModal.recommendation?.name || ""}
        onClose={handleContactModalClose}
        onSubmit={(email, customRequest) =>
          handleContactSubmit(email, customRequest, answers, journeyData)
        }
      />

      {/* General Contact Modal */}
      <ContactModal
        actionName="General Contact"
        isOpen={generalContactModal}
        recommendationName="CrowdSec Recommendations"
        showOnboardingNote={true}
        onClose={() => setGeneralContactModal(false)}
        onSubmit={handleGeneralContactSubmit}
      />

      {/* Notify Modal */}
      <NotifyModal
        actionName={notifyModal.action?.name || ""}
        isOpen={notifyModal.isOpen}
        recommendationName={notifyModal.recommendation?.name || ""}
        onClose={handleNotifyModalClose}
        onSubmit={(email) => handleNotifySubmit(email, answers, journeyData)}
      />
    </div>
  );
}
